import * as Sentry from '@sentry/react'

/**
 * Initialize Sentry error tracking
 * Only enabled in production to avoid noise during development
 */
export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production'

  // Skip initialization if no DSN or in development
  if (!dsn || import.meta.env.DEV) {
    return
  }

  Sentry.init({
    dsn,
    environment,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    enabled: import.meta.env.PROD,

    // Error tracking only - no performance monitoring
    tracesSampleRate: 0,

    // Session replay on errors only
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Ignore browser extension and non-actionable errors
    denyUrls: [/extensions\//i, /.*\.chrome\.com/i],

    beforeSend(event, hint) {
      // Drop noisy non-actionable browser errors
      const message = hint.originalException instanceof Error ? hint.originalException.message : ''
      if (message.includes('ResizeObserver')) return null
      if (message.includes('Failed to fetch dynamically imported module')) return null

      // Scrub sensitive headers if present
      if (event.request?.headers) {
        const headers = { ...event.request.headers }
        delete headers.authorization
        delete headers.cookie
        delete headers['x-clerk-token']
        event.request.headers = headers
      }

      return event
    },
  })
}

/**
 * Set user context in Sentry (call after Clerk auth loads)
 */
export function setSentryUser(user: {
  id: string
  email?: string | null
  username?: string | null
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email || undefined,
    username: user.username || undefined,
  })
}

/**
 * Clear user context in Sentry (call on logout)
 */
export function clearSentryUser() {
  Sentry.setUser(null)
}

/**
 * Set organization context tag
 */
export function setSentryOrganization(org: { id: string; name: string }) {
  Sentry.setTag('organization_id', org.id)
  Sentry.setTag('organization_name', org.name)
}

/**
 * Capture an exception with optional context
 */
export function captureError(
  error: Error,
  context?: {
    tags?: Record<string, string>
    extra?: Record<string, unknown>
  }
) {
  Sentry.captureException(error, {
    tags: context?.tags,
    extra: context?.extra,
  })
}

/**
 * Test function to verify Sentry is working
 * Call from browser console: window.testSentry()
 */
export function testSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN
  const isDev = import.meta.env.DEV
  const isProd = import.meta.env.PROD

  console.log('🔍 Sentry Debug Info:')
  console.log('  DSN configured:', dsn ? '✅ Yes' : '❌ No')
  console.log('  Environment:', isDev ? 'Development (Sentry DISABLED)' : 'Production')
  console.log('  Mode:', isProd ? 'PROD' : 'DEV')

  if (!dsn) {
    console.error('❌ VITE_SENTRY_DSN is not set. Add it to your environment.')
    return false
  }

  if (isDev) {
    console.warn('⚠️ Sentry is disabled in development. Deploy to production to test.')
    return false
  }

  // Send a test error
  try {
    Sentry.captureException(new Error('Test Sentry Integration - This is a test error'))
    console.log('✅ Test error sent to Sentry! Check your dashboard in 1-2 minutes.')
    return true
  } catch (e) {
    console.error('❌ Failed to send test error:', e)
    return false
  }
}

// Expose test function globally for console access
if (typeof window !== 'undefined') {
  (window as any).testSentry = testSentry
}
